import {
    MetaImageSize,
    MetaImage,
    LocationPageObject,
} from 'locations/lib/types';
import { FaqItem } from 'lib/types';
import { BlogEntry } from 'blog/lib/types';
import socialLinks from 'core/components/Social/links';
import {
    ImageObject,
    LocationMetaData,
    ArticleMetaData,
    VeterinaryCareMetaData,
    MetaData,
    Logo,
    SchemaType,
    Organization,
    RichSnippetsDataType,
} from './types';
import { formatHours } from '../../lib/util';

export function createImageObject(
    { src, width, height }: MetaImageSize,
    siteUrl: string,
): ImageObject {
    const url = `${siteUrl}${src}`;
    return {
        '@type': 'ImageObject',
        contentUrl: url,
        url,
        width,
        height,
    };
}

export function formatPhoneNumber(phoneNumber?: string): string | undefined {
    if (!phoneNumber?.startsWith('+')) {
        console.error('Invalid phone number format.');
        return undefined;
    }
    return phoneNumber.replace(/(\+\d)(\d{3})(\d{3})(\d+)/, '$1-$2-$3-$4');
}

export function getMetaImages(
    siteUrl: string,
    metaImages?: ReadonlyArray<MetaImage> | null,
): ReadonlyArray<ImageObject> | undefined {
    return metaImages?.length
        ? metaImages.flatMap(({ file: { childImageSharp } }) => [
              createImageObject(childImageSharp.square, siteUrl),
              createImageObject(childImageSharp.narrow, siteUrl),
              createImageObject(childImageSharp.wide, siteUrl),
          ])
        : undefined;
}

export function cleanString(inputString: string): string {
    return inputString
        .replace(/<\/?[^>]+(>|$)/g, '')
        .replace(/\s{2,}/g, ' ')
        .trim();
}

export function getLocationMetaData(
    siteUrl: string,
    location: LocationPageObject,
    faqItems?: FaqItem[],
    logo?: Logo,
): LocationMetaData {
    const url = `${siteUrl}/c/${location.slug}`;
    const clinicSlug = `${(location.slug ?? '').replace(/-animal-hospital$/, '')}`;

    const parentOrganization: Organization = {
        '@type': 'Organization',
        '@id': siteUrl,
        name: 'Bond Vet',
    };

    if (logo) {
        parentOrganization.image = [
            createImageObject(logo.childImageSharp.big, siteUrl),
            createImageObject(logo.childImageSharp.small, siteUrl),
        ];
    }

    const meta: LocationMetaData = {
        '@context': 'https://schema.org',
        '@type': 'VeterinaryCare',
        '@id': url,
        name: `Bond Vet - ${location.name}`,
        url,
        areaServed: {
            '@type': 'LocalBusiness',
            name: `Bond Vet - ${location.name}`,
            description: location.description,
            image: getMetaImages(siteUrl, location.metaImages),
            url,
            telephone: formatPhoneNumber(location.intlPhoneNumber),
            priceRange: '$$',
            hasMap: `https://g.page/bond-vet---${clinicSlug}`,
            parentOrganization,
        },
    };

    if (location.locationHours) {
        meta.areaServed.openingHours = formatHours(location.locationHours);
    }

    if (location.googleReviews) {
        meta.areaServed.aggregateRating = {
            '@type': 'AggregateRating',
            bestRating: location.googleReviews.bestRating,
            ratingValue: location.googleReviews.averageRating,
            reviewCount: location.googleReviews.totalReviewCount,
            worstRating: location.googleReviews.worstRating,
        };
    }

    if (location.fullAddress) {
        meta.areaServed.address = {
            '@type': 'PostalAddress',
            addressLocality: location.fullAddress.city,
            addressRegion: location.fullAddress.state,
            postalCode: location.fullAddress.zipCode,
            streetAddress: location.fullAddress.streetAddress,
        };
    }

    if (location.coordinates) {
        meta.areaServed.geo = {
            '@type': 'GeoCoordinates',
            latitude: location.coordinates.latitude,
            longitude: location.coordinates.longitude,
        };
    }

    if (faqItems) {
        meta.subjectOf = {
            '@type': 'FAQPage',
            mainEntity: faqItems.map(({ title, text }) => ({
                '@type': 'Question',
                url: `${url}#:~:text=${title.replace(/ /g, '%20').replace(/\?/g, '')}`,
                name: title,
                acceptedAnswer: {
                    '@type': 'Answer',
                    text: cleanString(text),
                },
            })),
        };
    }

    return meta;
}

export function getArticleMetaData(
    siteUrl: string,
    article: BlogEntry,
    slug?: string,
): ArticleMetaData {
    return {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: article.title,
        url: `${siteUrl}${slug ?? ''}`,
        image: article.image?.file?.publicURL
            ? `${siteUrl}${article.image.file.publicURL}`
            : '',
        datePublished: new Date(article.date)
            .toISOString()
            .replace('.000Z', '+00:00'),
        author: article.author?.teamMember
            ? {
                  '@type': 'Person',
                  name: article.author.teamMember?.name,
                  url: `${siteUrl}/experts`,
              }
            : {
                  '@type': 'Organization',
                  name: 'Bond Vet',
                  url: `${siteUrl}/experts`,
              },
    };
}

// [CL-1914] - Hardcoded values for now
export function getVeterinaryCareMetaData(
    siteUrl: string,
): VeterinaryCareMetaData {
    return {
        '@context': 'https://schema.org',
        '@type': 'VeterinaryCare',
        '@id': siteUrl,
        name: 'Bond Vet',
        url: siteUrl,
        // eslint-disable-next-line max-len
        image: 'https://bondvet.com/static/5a53fed8098e81733184a3692d29cccd/6e21a/website-uploads-Bond%20Vet%20Animal%20Hospital_Vet%20and%20pet%20parent%20in%20exam%20room-iOZEkguX6O5IgJhEUKYo6ELKu5CUVihAcTe3CfXYSSu8p1dn1fTyNLPC4nAM3Cpt.jpg-b95d2ba3.webp',
        // eslint-disable-next-line max-len
        logo: 'https://shop.bondvet.com/cdn/shop/files/Bond_Lockup_Primary_Positive_RGB_1200x1200.png?v=1614316876',
        description:
            // eslint-disable-next-line max-len
            'At Bond Vet, our mission is to strengthen the human-animal bond through pet health. We started Bond to make vet care better for the care team, clients, and patients. Our urgent-care and walk-in clinics are warm, friendly, and modern, designed with specific attention to a welcoming atmosphere. Bond Vet is changing the veterinary industry by building a team of innovators and creators.',
        email: 'team@bondvet.com',
        telephone: '(212) 430-8335',
        foundingDate: '2018-06-01',
        isAcceptingNewPatients: 'true',
        areaServed: [
            {
                '@type': 'Place',
                name: 'Atlanta, GA',
                url: `${siteUrl}/regions/atlanta`,
            },
            {
                '@type': 'Place',
                name: 'Boston, MA',
                url: `${siteUrl}/regions/boston`,
            },
            {
                '@type': 'Place',
                name: 'Chicago, IL',
                url: `${siteUrl}/regions/chicago`,
            },
            {
                '@type': 'Place',
                name: 'Connecticut',
                url: `${siteUrl}/regions/connecticut`,
            },
            {
                '@type': 'Place',
                name: 'D.C. Area',
                url: `${siteUrl}/regions/washington`,
            },
            {
                '@type': 'Place',
                name: 'Long Island, NY',
                url: `${siteUrl}/regions/long-island`,
            },
            {
                '@type': 'Place',
                name: 'New Jersey',
                url: `${siteUrl}/regions/new-jersey`,
            },
            {
                '@type': 'Place',
                name: 'New York City, NY',
                url: `${siteUrl}/regions/new-york-city`,
            },
            {
                '@type': 'Place',
                name: 'Philadelphia, PA',
                url: `${siteUrl}/regions/philadelphia`,
            },
            {
                '@type': 'Place',
                name: 'Westchester, NY',
                url: `${siteUrl}/regions/westchester`,
            },
        ],
        sameAs: socialLinks.map((link) => link.url),
        makesOffer: [
            {
                '@type': 'Offer',
                itemOffered: {
                    '@type': 'Service',
                    name: 'Urgent Care',
                    url: `${siteUrl}/veterinary-clinic-services/urgent-care`,
                },
            },
            {
                '@type': 'Offer',
                itemOffered: {
                    '@type': 'Service',
                    name: 'Preventive Care',
                    url: `${siteUrl}/veterinary-clinic-services/preventive-care`,
                },
            },
            {
                '@type': 'Offer',
                itemOffered: {
                    '@type': 'Service',
                    name: 'Puppy Visits',
                    url: `${siteUrl}/veterinary-clinic-services/puppy-visits`,
                },
            },
            {
                '@type': 'Offer',
                itemOffered: {
                    '@type': 'Service',
                    name: 'Kitten Visits',
                    url: `${siteUrl}/veterinary-clinic-services/kitten-visits`,
                },
            },
            {
                '@type': 'Offer',
                itemOffered: {
                    '@type': 'Service',
                    name: 'Surgeries',
                    url: `${siteUrl}/surgery`,
                },
            },
            {
                '@type': 'Offer',
                itemOffered: {
                    '@type': 'Service',
                    name: 'Dental Care',
                    url: `${siteUrl}/surgery/dental-care`,
                },
            },
            {
                '@type': 'Offer',
                itemOffered: {
                    '@type': 'Service',
                    name: 'Telehealth',
                    url: `${siteUrl}/telehealth`,
                },
            },
            {
                '@type': 'Offer',
                itemOffered: {
                    '@type': 'Service',
                    name: 'Travel Certificates',
                    url: `${siteUrl}/pet-travel`,
                },
            },
            {
                '@type': 'Offer',
                itemOffered: {
                    '@type': 'Service',
                    name: 'BondRx Pharmacy',
                    url: 'https://app.bondvet.com/home/online-pharmacy',
                },
            },
        ],
    };
}

export function getMetaData(
    siteUrl: string,
    schemaType: SchemaType,
    data?: RichSnippetsDataType,
    faqItems?: FaqItem[],
    logo?: Logo,
    slug?: string,
): MetaData | null {
    switch (schemaType) {
        case 'Location':
            return getLocationMetaData(
                siteUrl,
                data as LocationPageObject,
                faqItems,
                logo,
            );
        case 'Article':
            return getArticleMetaData(siteUrl, data as BlogEntry, slug);
        case 'VeterinaryCare':
            return getVeterinaryCareMetaData(siteUrl);
        default:
            console.error(`Unsupported schema type: ${schemaType}`);
            return null;
    }
}
